import React, { useEffect, useMemo, useRef } from 'react';
import { View, Animated } from 'react-native';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/therapist/Button';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { TextLink } from '@/components/TextLink';
import { Links } from '@/config/links';
import { LicenseType } from '@/api/services/user';

interface License {
  daysLeft: number;
}

interface LicenseBannerProps {
  showLicenseWarning: boolean;
  license: License;
  onRemindTomorrow: () => void;
  onHideForever: () => void;
  licenseType?: LicenseType;
}

export const LicenseBanner: React.FC<LicenseBannerProps> = ({
  showLicenseWarning,
  license,
  onRemindTomorrow,
  onHideForever,
  licenseType,
}) => {
  const [visible, setVisible] = React.useState(showLicenseWarning);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!showLicenseWarning) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: -300,
          duration: 500,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setVisible(false);
      });
    }
  }, [showLicenseWarning]);

  const message = useMemo(() => {
    if (licenseType === 'trial') {
      if (license.daysLeft === 0) {
        return (
          <Text>
            Die Testphase der Patienten-App endet{' '}
            <Text style={{ fontWeight: '700' }}>heute.</Text> Bitte erinnern Sie
            Ihre:n Patient:in daran, einen Rezeptcode einzulösen oder eine
            Lizenz zu buchen, um Unterbrechungen im Eigentraining zu vermeiden.
            {'\n'}
            {'\n'}
            Alle Infos zur Kostenerstattung{' '}
            <TextLink href={Links.diga.href}>hier.</TextLink>
          </Text>
        );
      }

      if (license.daysLeft < 0) {
        return (
          <Text>
            Die Testphase der Patienten-App ist abgelaufen. Bitte erinnern Sie
            Ihre:n Patient:in, einen Rezeptcode bei der Krankenkasse zu
            beantragen oder eine Lizenz zu buchen, um das Eigentraining
            fortsetzen zu können.
            {'\n'}
            {'\n'}
            Alle Infos zur Kostenerstattung{' '}
            <TextLink href={Links.diga.href}>hier.</TextLink>
          </Text>
        );
      }

      return (
        <Text>
          Die Testphase der Patienten-App endet in{' '}
          <Text style={{ fontWeight: '700' }}>
            {license.daysLeft} {license.daysLeft === 1 ? 'Tag' : 'Tagen'}.
          </Text>{' '}
          Bitte erinnern Sie Ihre:n Patient:in daran, einen Rezeptcode bei der
          Krankenkasse zu beantragen oder eine Lizenz zu buchen, um
          Unterbrechungen im Eigentraining zu vermeiden.
          {'\n'}
          {'\n'}
          Alle Infos zur Kostenerstattung{' '}
          <TextLink href={Links.diga.href}>hier.</TextLink>
        </Text>
      );
    }
    if (license.daysLeft === 0) {
      return (
        <Text>
          Die Patienten-App zum Eigentraining läuft{' '}
          <Text style={{ fontWeight: '700' }}>heute</Text> aus. Erinnern Sie
          Ihre:n Patient:in bitte an die Erneuerung der Lizenz und vermeiden Sie
          ungewollte Pausen beim Üben zuhause. Alle Infos zur Kostenerstattung{' '}
          <TextLink href={Links.diga.href}>hier.</TextLink>
          {'\n'}
          {'\n'}
          <Text style={{ fontWeight: '700' }}>Gut zu wissen:</Text>
          {'\n'}
          Gesetzlich Versicherte können den neuen Rezeptcode vorplanen. Er wird
          automatisch aktiviert, sobald der alte ausgelaufen ist.
        </Text>
      );
    } else if (license.daysLeft < 0) {
      return (
        <Text>
          Die Patienten-App zum Eigentraining ist abgelaufen. Damit Ihr:e
          Patient:in zu Hause weiter üben kann, erinnern Sie diese:n bitte
          daran, die Lizenz zu erneuern. Alle Infos zur Kostenerstattung{' '}
          <TextLink href={Links.diga.href}>hier.</TextLink>
        </Text>
      );
    } else {
      return (
        <Text>
          Die Patienten-App zum Eigentraining läuft in{' '}
          <Text style={{ fontWeight: '700' }}>
            {license.daysLeft} {license.daysLeft === 1 ? 'Tag' : 'Tagen'}
          </Text>{' '}
          aus. Erinnern Sie Ihre:n Patient:in bitte an die Erneuerung der Lizenz
          und vermeiden Sie ungewollte Pausen beim Üben zuhause. Alle Infos zur
          Kostenerstattung <TextLink href={Links.diga.href}>hier.</TextLink>
          {'\n'}
          {'\n'}
          <Text style={{ fontWeight: '700' }}>Gut zu wissen:</Text>
          {'\n'}
          Gesetzlich Versicherte können den neuen Rezeptcode vorplanen. Er wird
          automatisch aktiviert, sobald der alte ausgelaufen ist.
        </Text>
      );
    }
  }, [license.daysLeft]);

  if (!visible) {
    return null;
  }

  return (
    <Animated.View
      style={[
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Banner title="Ablauf Patienten-App" icon="warningOutline">
        <View>
          <Text style={{ color: colors.newUI.therapist.orange90 }}>
            {message}
          </Text>
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 16 }}>
            <Button
              label="Später erneut anzeigen"
              onPress={onRemindTomorrow}
              look="warning"
            />
            <Button
              label="Nicht mehr anzeigen"
              onPress={onHideForever}
              look="warningTertiary"
            />
          </View>
        </View>
      </Banner>
    </Animated.View>
  );
};
